import type { Article, Image, Map, Resource, Url } from "../types/content";

import { getStaticMapSrc } from "../components/Map/utils/getStaticMapSrc";

const imageServer =
  process.env.NEXT_PUBLIC_IMAGE_SERVER || "https://gfx.omni.se";
const watermarkServer = "https://watermark-gfx.omni.se";
const maxImageDimension = 2000;

type Size =
  | "large"
  | "small"
  | "expanded"
  | "sidebar"
  | "1x1"
  | "1x2"
  | "share"
  | "original";

type IdFields = Pick<Image, "image_asset" | "source_id" | "watermarks">;

type ImageProperties = {
  height: number;
  width: number;
  tight?: boolean;
};

type SizeMapping = { [key in Size]: ImageProperties };

type ResponsiveImageAttributes = {
  src: string;
  srcSet: string;
  sizes: string;
  width: number;
  height: number;
};

type ArticleEditorialImage =
  | {
      alt: string;
      image: Image;
      type: "image";
    }
  | {
      alt: string;
      type: "external";
      url: string;
    }
  | {
      alt: string;
      map: Map;
      type: "map";
    };

type ArticleMetadataImage = {
  alt: string;
  height?: number;
  url: string;
  width?: number;
};

const getImageAltText = (
  image: Pick<Image, "alternate_text" | "caption">,
): string => image.alternate_text?.value || image.caption?.value || "";

const articleMetadataImageSizes = [
  { height: 720, watermarkFormat: "2x1", width: 1280 },
  { height: 900, watermarkFormat: "2x1", width: 1200 },
  { height: 1200, watermarkFormat: "1x1", width: 1200 },
] as const;

const sizeNameToSizeValues: SizeMapping = {
  large: { width: 1376, height: 688, tight: false },
  expanded: { width: 1200, height: 600 },
  small: { width: 180, height: 180, tight: true },
  sidebar: { width: 80, height: 80, tight: true },
  "1x1": { width: 1372, height: 1372, tight: true },
  "1x2": { width: 640, height: 320, tight: true },
  share: { width: 1200, height: 628, tight: false },
  original: { width: 0, height: 0 },
};

const responsiveImageWidths = {
  large: [320, 480, 640, 960, 1120, 1280, 1376],
  "1x1": [240, 320, 480, 640, 960, 1372],
} as const;

const responsiveFeedImageWidths = {
  large: [320, 480, 640, 960, 1376],
  small: [100, 120, 180],
} as const;

const getImageSizeValues = (size: Size, image?: IdFields): ImageProperties => {
  if (size === "original" && image?.image_asset.size) {
    return {
      width: image.image_asset.size.width,
      height: image.image_asset.size.height,
    };
  }

  return sizeNameToSizeValues[size];
};

const getWatermarkUrl = (
  watermarkFormat: string,
  image?: Pick<Image, "watermarks">,
) => {
  return image?.watermarks?.find(
    (watermark) => watermark.format === watermarkFormat,
  )?.url;
};

const constrainImageDimensions = ({
  width,
  height,
}: {
  width: number;
  height: number;
}) => {
  const largestDimension = Math.max(width, height);
  if (largestDimension <= maxImageDimension) {
    return { width, height };
  }

  const scale = maxImageDimension / largestDimension;

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

const imageSrcForDimensions = ({
  width,
  height,
  tight,
  useFallback,
  image,
  watermarkFormat = "2x1",
}: {
  width: number;
  height: number;
  tight?: boolean | null;
  useFallback: boolean;
  image?: IdFields;
  watermarkFormat?: string;
}): string => {
  if (!image) {
    return "";
  }

  const tightQuery =
    tight !== null && tight !== undefined ? `&tight=${tight}` : "";
  const id = useFallback ? image.image_asset.id : image.source_id;
  const watermark = getWatermarkUrl(watermarkFormat, image);
  const constrainedDimensions = constrainImageDimensions({ width, height });

  if (watermark) {
    return `${watermarkServer}/images/${id}?h=${constrainedDimensions.height}${tightQuery}&w=${constrainedDimensions.width}&watermark=${watermark}`;
  }

  return `${imageServer}/images/${id}?h=${constrainedDimensions.height}${tightQuery}&w=${constrainedDimensions.width}`;
};

const imageSrc = (
  size: Size,
  useFallback: boolean,
  image?: IdFields,
): string => {
  if (!image) {
    return "";
  }

  const {
    width,
    height,
    tight: maybeTight = null,
  } = getImageSizeValues(size, image);

  return imageSrcForDimensions({
    width,
    height,
    tight: maybeTight,
    useFallback,
    image,
    // Keep legacy behavior for current callers.
    watermarkFormat: "2x1",
  });
};

const getResponsiveArticleImageAttributes = ({
  size,
  sizes,
  useFallback,
  image,
}: {
  size: "large" | "1x1" | "small" | "original";
  sizes: string;
  useFallback: boolean;
  image?: IdFields;
}): ResponsiveImageAttributes | null => {
  if (!image || (size !== "large" && size !== "1x1")) {
    return null;
  }

  const baseSize = getImageSizeValues(size, image);
  const candidateWidths = responsiveImageWidths[size];
  const watermarkFormat = size === "1x1" ? "1x1" : "2x1";

  const srcSet = candidateWidths
    .map((candidateWidth) => {
      const candidateHeight = Math.round(
        (candidateWidth * baseSize.height) / baseSize.width,
      );

      return `${imageSrcForDimensions({
        width: candidateWidth,
        height: candidateHeight,
        tight: baseSize.tight,
        useFallback,
        image,
        watermarkFormat,
      })} ${candidateWidth}w`;
    })
    .join(", ");

  return {
    src: imageSrcForDimensions({
      width: baseSize.width,
      height: baseSize.height,
      tight: baseSize.tight,
      useFallback,
      image,
      watermarkFormat,
    }),
    srcSet,
    sizes,
    width: baseSize.width,
    height: baseSize.height,
  };
};

const getResponsiveFeedImageAttributes = ({
  size,
  sizes,
  useFallback,
  image,
}: {
  size: "large" | "small";
  sizes: string;
  useFallback: boolean;
  image?: IdFields;
}): ResponsiveImageAttributes | null => {
  if (!image) {
    return null;
  }

  const baseSize = getImageSizeValues(size, image);
  const candidateWidths = responsiveFeedImageWidths[size];
  const watermarkFormat = size === "small" ? "1x1" : "2x1";

  const srcSet = candidateWidths
    .map((candidateWidth) => {
      const candidateHeight = Math.round(
        (candidateWidth * baseSize.height) / baseSize.width,
      );

      return `${imageSrcForDimensions({
        width: candidateWidth,
        height: candidateHeight,
        tight: baseSize.tight,
        useFallback,
        image,
        watermarkFormat,
      })} ${candidateWidth}w`;
    })
    .join(", ");

  return {
    src: imageSrcForDimensions({
      width: baseSize.width,
      height: baseSize.height,
      tight: baseSize.tight,
      useFallback,
      image,
      watermarkFormat,
    }),
    srcSet,
    sizes,
    width: baseSize.width,
    height: baseSize.height,
  };
};

const isValidArticleImage = (
  resource: Resource | undefined,
): resource is Image =>
  resource?.type === "Image" &&
  Boolean(resource.image_asset?.id) &&
  resource.image_asset.size.width > 0 &&
  resource.image_asset.size.height > 0;

const getVideoPosterUrl = (resource: Resource | undefined) => {
  if (resource?.type !== "Url" || resource.data?.type !== "video") {
    return undefined;
  }

  const posterUrl =
    resource.data.custom_properties?.thumbnail_url || resource.data.image;

  return isAbsoluteHttpUrl(posterUrl) ? posterUrl : undefined;
};

const isAbsoluteHttpUrl = (value: string | undefined): value is string => {
  if (!value) return false;

  try {
    const url = new URL(value);

    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
};

const getResourceEditorialImage = (
  resource: Resource | undefined,
): ArticleEditorialImage | undefined => {
  if (isValidArticleImage(resource)) {
    return {
      alt: getImageAltText(resource),
      image: resource,
      type: "image",
    };
  }

  if (resource?.type === "Map") {
    return { alt: "karta", map: resource, type: "map" };
  }

  if (resource?.type === "ExternalImage" && isAbsoluteHttpUrl(resource.url)) {
    return { alt: "", type: "external", url: resource.url };
  }

  const videoPosterUrl = getVideoPosterUrl(resource);

  if (videoPosterUrl) {
    return {
      alt: resource?.type === "Url" ? resource.title?.value || "" : "",
      type: "external",
      url: videoPosterUrl,
    };
  }

  return undefined;
};

const getTeaserEditorialImage = (
  article: Article,
): ArticleEditorialImage | undefined => {
  const teaserImage = article.teaser?.image;

  if (!teaserImage) return undefined;

  if (
    teaserImage.type === "ExternalImage" ||
    teaserImage.type === "Image" ||
    teaserImage.type === "Map" ||
    teaserImage.type === "Url"
  ) {
    return getResourceEditorialImage(teaserImage);
  }

  return undefined;
};

const getArticleEditorialImage = (
  article: Article,
): ArticleEditorialImage | undefined => {
  const resources = article.resources || [];
  const mainImage = getResourceEditorialImage(article.main_resource);
  if (mainImage) return mainImage;

  const leadResource = resources.find(
    (resource) =>
      (resource.type === "ExternalImage" ||
        resource.type === "Image" ||
        resource.type === "Map" ||
        resource.type === "Url") &&
      resource.subtype === "lead",
  );
  const leadImage = getResourceEditorialImage(leadResource);
  if (leadImage) return leadImage;

  const titleIndex = resources.findIndex(
    (resource) => resource.type === "Title",
  );
  const appearsBeforeTitle = (_resource: Resource, index: number) =>
    titleIndex === -1 || index < titleIndex;
  const bodyImage = resources.find(
    (resource, index) =>
      appearsBeforeTitle(resource, index) &&
      (isValidArticleImage(resource) || resource.type === "Map"),
  );
  if (bodyImage) return getResourceEditorialImage(bodyImage);

  const teaserImage = getTeaserEditorialImage(article);
  if (teaserImage) return teaserImage;

  const bodyVideo = resources.find(
    (resource, index): resource is Url =>
      appearsBeforeTitle(resource, index) &&
      resource.type === "Url" &&
      resource.data?.type === "video",
  );

  return getResourceEditorialImage(bodyVideo);
};

const getArticleMetadataImages = (article: Article): ArticleMetadataImage[] => {
  const editorialImage = getArticleEditorialImage(article);

  if (!editorialImage) return [];

  if (editorialImage.type === "external") {
    return [{ alt: editorialImage.alt, url: editorialImage.url }];
  }

  if (editorialImage.type === "map") {
    return articleMetadataImageSizes.map(({ height, width }) => ({
      alt: editorialImage.alt,
      height,
      url: getStaticMapSrc({ height, width }, editorialImage.map),
      width,
    }));
  }

  return articleMetadataImageSizes.map(
    ({ height, watermarkFormat, width }) => ({
      alt: editorialImage.alt,
      height,
      url: imageSrcForDimensions({
        height,
        image: editorialImage.image,
        tight: false,
        useFallback: true,
        watermarkFormat,
        width,
      }),
      width,
    }),
  );
};

const getExpectedLargeImage = (image: Image) => {
  const size = getImageSizeValues("large", image);

  return {
    url: imageSrc("large", true, image),
    width: size.width,
    height: size.height,
    alt: image.caption?.value || "",
  };
};

export {
  getArticleEditorialImage,
  getArticleMetadataImages,
  getExpectedLargeImage,
  getImageAltText,
  getImageSizeValues,
  getResponsiveArticleImageAttributes,
  getResponsiveFeedImageAttributes,
  imageSrc,
  imageSrcForDimensions,
  sizeNameToSizeValues,
};
